import { FormCheckbox } from "@app/components/forms/FormCheckbox";
import { FormCheckboxGroup } from "@app/components/forms/FormCheckboxGroup";
import { FormColor } from "@app/components/forms/FormColor";
import { FormInput } from "@app/components/forms/FormInput";
import { FormMultiSelect } from "@app/components/forms/FormMultiSelect";
import { FormNumber } from "@app/components/forms/FormNumber";
import { FormPassword } from "@app/components/forms/FormPassword";
import { FormRadioGroup } from "@app/components/forms/FormRadioGroup";
import { FormRange } from "@app/components/forms/FormRange";
import { FormSelect } from "@app/components/forms/FormSelect";
import { FormSwitch } from "@app/components/forms/FormSwitch";
import { FormTextarea } from "@app/components/forms/FormTextarea";
import {
  FormFieldSchema,
  FormFieldType,
  ItemGroupSchema,
} from "@app/components/forms/types/schema";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Form,
  Separator,
} from "@shortsworks/ui";
import { cn } from "@shortsworks/ui/lib/utils";
import { forwardRef } from "react";
import { UseFormReturn } from "react-hook-form";

interface FormContainerProps {
  schemas: FormFieldSchema[];
  form: UseFormReturn<any>;
  className?: string;
}

const isContainsPasswordField = (schemas: FormFieldSchema[]): boolean => {
  return schemas.some((schema) => schema.type === FormFieldType.Password);
};

const FormContainer = forwardRef<any, FormContainerProps>((props, ref) => {
  return (
    <section className={cn("flex flex-col gap-4 px-[1px]", props.className)}>
      <Form ref={ref} {...props.form}>
        {render(props.schemas, props.form)}
      </Form>
    </section>
  );
});

const render = (schemas: FormFieldSchema[], form: UseFormReturn<any>) => {
  return schemas.map((schema, index) => {
    // if(schema.behavior?.hidden) { return <></>; }

    switch (schema.type) {
      case FormFieldType.Text:
        return (
          <FormInput
            key={`${schema.id}-${index}`}
            form={form}
            schema={schema}
          />
        );
      case FormFieldType.URL:
        return (
          <FormInput
            key={`${schema.id}-${index}`}
            form={form}
            schema={schema}
          />
        );
      case FormFieldType.Textarea:
        return (
          <FormTextarea
            key={`${schema.id}-${index}`}
            form={form}
            schema={schema}
          />
        );
      case FormFieldType.Email:
        return (
          <FormInput
            key={`${schema.id}-${index}`}
            form={form}
            schema={schema}
          />
        );
      case FormFieldType.Password:
        return (
          <FormPassword
            key={`${schema.id}-${index}`}
            form={form}
            schema={schema}
          />
        );
      case FormFieldType.Number:
        return (
          <FormNumber
            key={`${schema.id}-${index}`}
            form={form}
            schema={schema}
          />
        );
      case FormFieldType.Select:
        return (
          <FormSelect
            key={`${schema.id}-${index}`}
            form={form}
            schema={schema}
          />
        );
      case FormFieldType.MultiSelect:
        return (
          <FormMultiSelect
            key={`${schema.id}-${index}`}
            form={form}
            schema={schema}
          />
        );
      case FormFieldType.Switch:
        return (
          <FormSwitch
            key={`${schema.id}-${index}`}
            form={form}
            schema={schema}
          />
        );
      case FormFieldType.RadioGroup:
        return (
          <FormRadioGroup
            key={`${schema.id}-${index}`}
            form={form}
            schema={schema}
          />
        );
      case FormFieldType.CheckboxGroup:
        return (
          <FormCheckboxGroup
            key={`${schema.id}-${index}`}
            form={form}
            schema={schema}
          />
        );
      case FormFieldType.Checkbox:
        return (
          <FormCheckbox
            key={`${schema.id}-${index}`}
            form={form}
            schema={schema}
          />
        );
      case FormFieldType.Color:
        return (
          <FormColor
            key={`${schema.id}-${index}`}
            form={form}
            schema={schema}
          />
        );
      case FormFieldType.Range:
        return (
          <FormRange
            key={`${schema.id}-${index}`}
            form={form}
            schema={schema}
          />
        );
      case FormFieldType.Separator:
        return renderSeparator(schema, index);
      case FormFieldType.Group:
        return renderGroup(schema, schema.schemas, form);
    }
  });
};

const renderGroup = (
  schema: ItemGroupSchema,
  schemas: FormFieldSchema[],
  form: UseFormReturn<any>,
) => {
  return (
    <Card
      key={`group-${schema.id}`}
      className={cn(
        "rounded-md shadow-sm p-3 bg-transparent",
        schema.noneStyle && "border-0 shadow-none p-0",
        schema.behavior?.hidden && "hidden",
      )}
    >
      {(schema.label || schema.description) && (
        <CardHeader className="p-0 pb-6">
          {schema.label && <CardTitle>{schema.label}</CardTitle>}
          {schema.description && (
            <CardDescription>{schema.description}</CardDescription>
          )}
        </CardHeader>
      )}
      <CardContent className="flex flex-col gap-4 p-0">
        {render(schemas, form)}
      </CardContent>
    </Card>
  );
};

const renderSeparator = (schema: FormFieldSchema, index: number) => {
  return (
    <Separator
      key={`separator-${index}`}
      className={cn(
        schema.appearance?.className,
        schema.behavior?.hidden && "hidden",
      )}
    />
  );
};

export default FormContainer;
